import { useRef, useState } from "react";
import { Camera, X, Upload } from "lucide-react";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { compressImage } from "@/utils/imageHelpers";

interface ImageCaptureProps {
  onImageCapture: (file: File, preview: string) => void;
  onRemove: () => void;
  value?: string; // Preview URL
  maxSizeMB?: number; // Default 1MB
}

export const ImageCapture = ({
  onImageCapture,
  onRemove,
  value,
  maxSizeMB = 1,
}: ImageCaptureProps) => {
  const [isCompressing, setIsCompressing] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleFileSelect = async (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    const file = event.target.files?.[0];
    if (!file) return;

    // Validate file type
    if (!file.type.startsWith("image/")) {
      toast.error("Vennligst velg en bildefil");
      return;
    }

    // Check file size (before compression)
    const fileSizeMB = file.size / (1024 * 1024);
    if (fileSizeMB > 10) {
      toast.error("Bildet er for stort (maks 10MB)");
      return;
    }

    try {
      setIsCompressing(true);

      // Compress if needed
      let processedFile = file;
      if (fileSizeMB > maxSizeMB) {
        toast.info("Komprimerer bilde...");
        processedFile = await compressImage(file);
        const compressedSizeMB = processedFile.size / (1024 * 1024);
        console.log(
          `Compressed from ${fileSizeMB.toFixed(2)}MB to ${compressedSizeMB.toFixed(2)}MB`
        );
      }

      // Create preview
      const reader = new FileReader();
      reader.onload = (e) => {
        const preview = e.target?.result as string;
        onImageCapture(processedFile, preview);
      };
      reader.readAsDataURL(processedFile);
    } catch (error) {
      console.error("Error processing image:", error);
      toast.error("Kunne ikke behandle bildet");
    } finally {
      setIsCompressing(false);
    }
  };

  const handleRemove = () => {
    onRemove();
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  return (
    <div className="space-y-2">
      {!value ? (
        <div className="flex gap-2">
          {/* Camera/Gallery input */}
          <Button
            type="button"
            variant="outline"
            className="flex-1"
            onClick={() => fileInputRef.current?.click()}
            disabled={isCompressing}
          >
            <Camera className="h-4 w-4 mr-2" />
            {isCompressing ? "Behandler..." : "Ta bilde"}
          </Button>

          <Button
            type="button"
            variant="outline"
            className="flex-1"
            onClick={() => fileInputRef.current?.click()}
            disabled={isCompressing}
          >
            <Upload className="h-4 w-4 mr-2" />
            Velg fra galleri
          </Button>

          <input
            ref={fileInputRef}
            type="file"
            accept="image/*"
            capture="environment"
            onChange={handleFileSelect}
            className="hidden"
          />
        </div>
      ) : (
        <div className="relative w-full aspect-video rounded-lg overflow-hidden bg-muted">
          <img
            src={value}
            alt="Preview"
            className="w-full h-full object-cover"
          />
          <Button
            type="button"
            variant="destructive"
            size="icon"
            className="absolute top-2 right-2"
            onClick={handleRemove}
          >
            <X className="h-4 w-4" />
          </Button>
        </div>
      )}
    </div>
  );
};
